import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  InputAdornment,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as ContainerIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useCompanyCore } from '../../../../core/modules/company';
import { ReceptionContainer } from '../../../../core/interfaces/receptionCotnainer.interface';

// Helper para traducir estados de contenedores de origen
const translateOriginContainerStatus = (statusName: string): string => {
  const statusTranslations: { [key: string]: string } = {
    'Unprocessed': 'Sin procesar',
    'In process': 'En proceso',
    'Paused': 'Pausado',
    'Emptied': 'Vacío',
  };
  return statusTranslations[statusName] || statusName;
};

interface ReceptionContainerSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (container: ReceptionContainer) => void;
  title: string;
  companyId?: number;
}

export const ReceptionContainerSearchDialog: React.FC<ReceptionContainerSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  companyId,
}) => {
  const { getAllReceptionContainers } = useReceptionCore();
  const { companyId: contextCompanyId } = useCompanyCore();

    // Estado local
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  
  // Usar companyId del contexto si no se proporciona
  const effectiveCompanyId = companyId || contextCompanyId;

  // Estado para el resultado de búsqueda
  const [searchResult, setSearchResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [receptionContainers, setReceptionContainers] = useState<any[]>([]);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  
  // Generar opciones de estado dinámicamente
  const statusOptions = useMemo(() => {
    if (!receptionContainers || receptionContainers.length === 0) return [];
    
    const uniqueStatuses = new Set<string>();
    receptionContainers.forEach(container => {
      if (container.origin_container_status?.name) {
        uniqueStatuses.add(container.origin_container_status.name);
      }
    });
    
    return Array.from(uniqueStatuses).sort();
  }, [receptionContainers]);

  const providerOptions = useMemo(() => {
    if (!receptionContainers || receptionContainers.length === 0) return [];

    const uniqueProviders = new Map<number, string>();
    receptionContainers.forEach((container) => {
      if (container.supplier_id) {
        const displayName =
          container.supplier_name ||
          container.supplier_business_name ||
          `Proveedor ${container.supplier_id}`;
        uniqueProviders.set(container.supplier_id, displayName);
      }
    });

    return Array.from(uniqueProviders.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [receptionContainers]);

  // Limpiar estado cuando se cierra
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedStatus('all');
      setSelectedProvider('all');
    }
  }, [open]);

  // Cargar contenedores de recepción cuando se abre el diálogo
  useEffect(() => {
    if (open && effectiveCompanyId) {
      setLoading(true);
      // Usar la nueva función del hook que trae todos los contenedores de recepción
      getAllReceptionContainers(
        effectiveCompanyId,
        1,
        100,
                 (containers) => {
           console.log('Containers loaded:', containers.length);
           console.log('First container sample:', containers[0]);
           setReceptionContainers(containers);
           setLoading(false);
         },
        () => {
          console.error('Error loading reception containers');
          setLoading(false);
        }
      );
    }
  }, [open, effectiveCompanyId, getAllReceptionContainers]);

  // Limpiar estado cuando se cierra
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedStatus('all');
      setSelectedProvider('all');
    }
  }, [open]);

  const handleContainerSelect = (reception: ReceptionContainer) => {
    onSelect(reception);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Función para copiar al portapapeles
  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000); // Ocultar después de 3 segundos
      console.log('Código copiado al portapapeles:', text);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  // Filtrar contenedores por término de búsqueda y estado
  const filteredReceptionContainers = useMemo(() => {
    if (!receptionContainers || receptionContainers.length === 0) return [];

    return receptionContainers.filter((reception) => {
      // Filtrar por término de búsqueda
      const matchesSearch = searchTerm === '' || 
        reception.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reception.reference?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtrar por estado usando el nombre del status
      const matchesStatus = selectedStatus === 'all' || 
        reception.origin_container_status?.name === selectedStatus;

      const matchesProvider =
        selectedProvider === 'all' ||
        `${reception.supplier_id}` === selectedProvider;

      return matchesSearch && matchesStatus && matchesProvider;
    });
  }, [receptionContainers, searchTerm, selectedStatus, selectedProvider]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 400,
          zIndex: 1300,
        },
      }}
      sx={{
        zIndex: 1300,
        '& .MuiBackdrop-root': {
          zIndex: 1299,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ContainerIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

             <DialogContent>
         <Box display="flex" flexDirection="column" gap={3}>
           {/* Toast de confirmación de copia */}
           {copySuccess && (
             <Alert severity="success" onClose={() => setCopySuccess(null)}>
               {copySuccess}
             </Alert>
           )}
           
           {/* Búsqueda por código */}
           <Box>
            <Typography variant="subtitle1" gutterBottom>
              Filtrar contenedores de recepción
            </Typography>
            
                         <Box display="flex" gap={2} alignItems="center">
               <TextField
                 fullWidth
                 placeholder="Buscar por código o referencia"
                 value={searchTerm}
                 onChange={handleSearchInputChange}
                 InputProps={{
                   startAdornment: (
                     <InputAdornment position="start">
                       <SearchIcon />
                     </InputAdornment>
                   ),
                 }}
               />
               <FormControl fullWidth>
                 <InputLabel>Estado</InputLabel>
                 <Select
                   value={selectedStatus}
                   onChange={(e) => setSelectedStatus(e.target.value)}
                   label="Estado"
                 >
                                       <MenuItem value="all">Todos los estados</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {translateOriginContainerStatus(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Proveedor</InputLabel>
                  <Select
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    label="Proveedor"
                  >
                    <MenuItem value="all">Todos los proveedores</MenuItem>
                    {providerOptions.map((provider) => (
                      <MenuItem key={provider.id} value={`${provider.id}`}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
             </Box>
          </Box>

          <Divider />

          {/* Resultado de búsqueda */}
          <Box>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : filteredReceptionContainers.length === 0 ? (
              <Alert severity="info">
                No se encontraron contenedores de recepción con los filtros aplicados.
              </Alert>
            ) : (
              <List>
                {filteredReceptionContainers.map((reception) => (
                  <ListItem
                    key={reception.id}
                    disablePadding
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleContainerSelect(reception)}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <ContainerIcon />
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                                                 primary={
                           <Box display="flex" alignItems="center" gap={1}>
                             <Typography variant="body1" fontWeight="medium">
                               {reception.code}
                             </Typography>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation(); // Evitar que se seleccione el contenedor
                                 handleCopyToClipboard(reception.code);
                               }}
                               sx={{
                                 color: 'text.secondary',
                                 '&:hover': {
                                   color: 'primary.main',
                                   backgroundColor: 'action.hover',
                                 },
                               }}
                               title="Copiar código al portapapeles"
                             >
                               <CopyIcon fontSize="small" />
                             </IconButton>
                             {reception.origin_container_status?.name && (
                               <Chip
                                 label={translateOriginContainerStatus(reception.origin_container_status.name)}
                                 color={
                                   reception.origin_container_status.name === 'Unprocessed' ? 'secondary' :
                                   reception.origin_container_status.name === 'In process' ? 'info' :
                                   reception.origin_container_status.name === 'Paused' ? 'warning' :
                                   reception.origin_container_status.name === 'Emptied' ? 'success' : 'default'
                                 }
                                 size="small"
                                 variant="outlined"
                               />
                             )}
                           </Box>
                         }
                                                 secondary={
                           <Box>
                             {reception.reference && (
                               <Typography variant="caption" display="block">
                                 Referencia: {reception.reference}
                               </Typography>
                             )}
                            {(reception.supplier_name || reception.supplier_business_name) && (
                              <Typography variant="caption" display="block">
                                Proveedor: {reception.supplier_name || reception.supplier_business_name}
                              </Typography>
                            )}
                            {reception.supplier_area_name && (
                              <Typography variant="caption" display="block">
                                Área: {reception.supplier_area_acronym ? `${reception.supplier_area_acronym} - ` : ''}
                                {reception.supplier_area_name}
                              </Typography>
                            )}
                           </Box>
                         }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
